import type { InventoryItem } from '../types/inventory';
import {
  barcodeToSvgString,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_PRINT_JS_OPTIONS,
} from './inventoryLabelBarcode';
import { escapeHtml, openPrintWindow, PRINT_POPUP_BLOCKED_MESSAGE } from './printWindow';

export interface InventoryLabelPrintRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
}

/**
 * Etiqueta 107×11 mm — layout absoluto desde el borde izquierdo:
 * adhesivo vacío 0→53 | info left 58 | barcode left 90.
 */
export const LABEL_PRINT_WIDTH_MM = 107;
export const LABEL_PRINT_HEIGHT_MM = 11;
/** Separación vertical entre etiquetas en un lote (mm). Individual (N=1) no aplica. */
export const LABEL_PRINT_GAP_MM = 8;
/** Zona adhesiva vacía (0 → este valor). Sin contenido impreso. */
export const LABEL_SEG_ADHESIVE_MM = 53;
/** Inicio de Plata/Bs. desde el borde izquierdo de la etiqueta (mm). */
export const LABEL_INFO_LEFT_MM = 58;
/** Inicio del código de barras + peso/código desde el borde izquierdo (mm). */
export const LABEL_BARCODE_LEFT_MM = 90;
/** Ancho útil de la zona info (hasta el barcode). */
export const LABEL_INFO_WIDTH_MM = LABEL_BARCODE_LEFT_MM - LABEL_INFO_LEFT_MM;
/** Ancho útil de la zona barcode (hasta el borde derecho). */
export const LABEL_BARCODE_WIDTH_MM = LABEL_PRINT_WIDTH_MM - LABEL_BARCODE_LEFT_MM;

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '—';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Abre ventana de impresión: todo el lote en UNA sola página.
 * Alto = N×11 mm + (N−1)×8 mm de separación. Individual (N=1) = 107×11, sin gap.
 * Posiciones absolutas en mm desde la izquierda (info 58, barcode 90).
 */
export function openInventoryLabelsPrintWindow(rows: InventoryLabelPrintRow[]): void {
  if (rows.length === 0) return;

  const sheetHeightMm =
    rows.length * LABEL_PRINT_HEIGHT_MM + Math.max(0, rows.length - 1) * LABEL_PRINT_GAP_MM;

  const labelsHtml = rows
    .map((row) => {
      const svg = barcodeToSvgString(row.productCodeUnique, LABEL_BARCODE_PRINT_JS_OPTIONS);
      return `<div class="label-page"><div class="label-info"><div class="mat">${escapeHtml(row.materialLabel)}</div><div class="price">${escapeHtml(formatPriceBs(row.salePrice))}</div></div><div class="label-barcode"><div class="barcode-wrap">${svg}</div><div class="subbar"><span class="weight">${escapeHtml(formatWeight(row.weightGrams))}</span><span class="code">${escapeHtml(row.productCodeUnique)}</span></div></div></div>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Etiquetas</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page {
      size: ${LABEL_PRINT_WIDTH_MM}mm ${sheetHeightMm}mm;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      font-family: 'Times New Roman', Times, serif;
      background: #fff;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .label-sheet {
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      height: ${sheetHeightMm}mm;
      max-height: ${sheetHeightMm}mm;
      margin: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      gap: ${LABEL_PRINT_GAP_MM}mm;
    }
    .label-page {
      position: relative;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      height: ${LABEL_PRINT_HEIGHT_MM}mm;
      max-height: ${LABEL_PRINT_HEIGHT_MM}mm;
      margin: 0;
      padding: 0;
      overflow: hidden;
      flex-shrink: 0;
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-after: avoid;
      break-after: avoid;
      page-break-before: avoid;
      break-before: avoid;
    }
    /* 0 → ${LABEL_SEG_ADHESIVE_MM} mm: adhesivo vacío (sin nodos) */
    .label-info {
      position: absolute;
      left: ${LABEL_INFO_LEFT_MM}mm;
      top: 0;
      width: ${LABEL_INFO_WIDTH_MM}mm;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;
    }
    .label-barcode {
      position: absolute;
      left: ${LABEL_BARCODE_LEFT_MM}mm;
      top: 0;
      width: ${LABEL_BARCODE_WIDTH_MM}mm;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.9mm;
      overflow: hidden;
    }
    .mat {
      font-style: italic;
      font-size: 10pt;
      line-height: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .price {
      font-size: 10pt;
      font-style: normal;
      line-height: 1;
      overflow: hidden;
      white-space: nowrap;
    }
    .barcode-wrap {
      flex: 1 1 auto;
      min-height: 0;
      max-height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm;
      width: 100%;
      overflow: hidden;
    }
    /* Height-driven size; width auto so CODE128 modules stay uniform (scannable). */
    .barcode-wrap svg {
      width: auto !important;
      max-width: 100%;
      height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm !important;
      max-height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm !important;
      display: block;
    }
    .subbar {
      flex: 0 0 auto;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: baseline;
      width: 100%;
      font-size: 10pt;
      line-height: 1;
      overflow: hidden;
    }
    .weight { flex: 0 0 auto; }
    .code { text-decoration: underline; text-align: right; overflow: hidden; }
    @media screen {
      body {
        width: ${LABEL_PRINT_WIDTH_MM}mm;
        margin: 0 auto;
      }
      .label-sheet {
        height: auto;
        max-height: none;
      }
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: ${LABEL_PRINT_WIDTH_MM}mm !important;
      }
      .label-sheet { margin: 0; }
    }
  </style>
</head>
<body><div class="label-sheet">${labelsHtml}</div></body>
</html>`;

  const printWindow = openPrintWindow({
    html,
    size: 'labels',
    autoPrint: true,
    windowName: 'printLabels',
  });

  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
}

/** Una etiqueta desde fila de almacén/sucursal (Gestión en almacén / Gestión en sucursal). */
export function printInventoryUnitLabel(item: InventoryItem): void {
  openInventoryLabelsPrintWindow([
    {
      productCodeUnique: String(item.id),
      materialLabel: item.material || '—',
      salePrice: item.price ?? 0,
      weightGrams: item.weightGrams ?? null,
    },
  ]);
}
